import React, {
  useReducer,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import api from '../lib/api';
import { AuthContext } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

// Import modular components
import { fadeIn, staggerContainer } from './flowers/animations';
import { reducer, initialState, ACTIONS } from './flowers/flowerReducer';
import FilterSection from './flowers/FilterSection';
import FlowerGrid from './flowers/FlowerGrid';
import SubscriptionPanel from './subscription/SubscriptionPanel';

// Main component that orchestrates the entire flower feature
const FeaturedFlowers = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showSubscriptionPanel, setShowSubscriptionPanel] = useState(false);
  const filterRef = useRef(null);
  const isMounted = useRef(true);
  const { user, currentUser } = useContext(AuthContext);

  // State for auth modal
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Create a specific handler for subscription button click
  const handleSubscribeButtonClick = () => {
    // Check if either user reference exists
    const authenticatedUser = user || currentUser;

    if (!authenticatedUser) {
      setShowAuthModal(true);
      return;
    }

    setShowSubscriptionPanel(true);
  };

  // Handle auth modal close
  const handleAuthModalClose = () => {
    setShowAuthModal(false);
  };

  // Handle successful authentication
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowSubscriptionPanel(true);
  };

  // Handle clicks outside the filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        state.filterDropdownOpen
      ) {
        dispatch({ type: ACTIONS.TOGGLE_FILTER_DROPDOWN, payload: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.filterDropdownOpen]);

  // Initialize component cleanup
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch flowers data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!isMounted.current) return;

      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_ERROR, payload: null }); // Clear any previous errors

      try {
        // Fetch all data in parallel
        const [flowersResponse, colorsResponse, categoriesResponse] =
          await Promise.all([
            api.get('/flowers'),
            api.get('/flowers/colors'),
            api.get('/flowers/categories'),
          ]);

        if (!isMounted.current) return;

        const flowersData = flowersResponse.data;
        if (!Array.isArray(flowersData)) {
          throw new Error('Invalid flowers data format received');
        }

        // Process flowers data
        const processedFlowers = flowersData.map((flower) => ({
          ...flower,
          quantity: 0,
          path: flower.image_url,
          pricePerStem: flower.price_per_stem,
        }));

        if (!isMounted.current) return;

        // Set filter options
        dispatch({
          type: ACTIONS.SET_FILTER_OPTIONS,
          payload: {
            colors: colorsResponse.data,
            categories: categoriesResponse.data,
          },
        });

        // Set flowers after filter options to ensure filtering works correctly
        dispatch({ type: ACTIONS.SET_FLOWERS, payload: processedFlowers });
      } catch (error) {
        if (!isMounted.current) return;

        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: 'Failed to load flowers. Please try again later.',
        });
      }
    };

    fetchData();
  }, []);

  // Action dispatchers
  const handleQuantityChange = (id, change) => {
    dispatch({
      type: ACTIONS.UPDATE_FLOWER_QUANTITY,
      payload: { id, change },
    });
  };

  const handleSetColorFilter = (color) => {
    dispatch({ type: ACTIONS.SET_COLOR_FILTER, payload: color });
  };

  const handleSetCategoryFilter = (category) => {
    dispatch({ type: ACTIONS.SET_CATEGORY_FILTER, payload: category });
  };

  const handleClearAllFilters = () => {
    dispatch({ type: ACTIONS.CLEAR_ALL_FILTERS });
  };

  const handleToggleFilterDropdown = (forcedState) => {
    dispatch({ type: ACTIONS.TOGGLE_FILTER_DROPDOWN, payload: forcedState });
  };

  // Prevent navigation/reload events when subscription panel is open
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (showSubscriptionPanel) {
        e.preventDefault();
        return (e.returnValue = '');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [showSubscriptionPanel]);

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
      className="py-16 bg-light/50"
      id="featured-flowers"
      data-testid="featured-flowers-section"
      onClick={(e) => {
        // Close filter dropdown if open and click is outside
        if (
          state.filterDropdownOpen &&
          filterRef.current &&
          !filterRef.current.contains(e.target)
        ) {
          dispatch({ type: ACTIONS.TOGGLE_FILTER_DROPDOWN, payload: false });
        }
      }}
    >
      <div className="container mx-auto px-4">
        <motion.div variants={fadeIn} className="text-center mb-12">
          <span className="text-secondary font-accent text-lg block mb-2">
            Handpicked Selection
          </span>
          <h2 className="text-4xl font-bold font-heading text-primary mb-4">
            Our Flowers
          </h2>
          <p className="text-text-light max-w-2xl mx-auto">
            Browse our curated selection of premium flowers and create your
            perfect arrangement. Add your favorites to your subscription for
            regular deliveries.
          </p>
        </motion.div>

        {/* Filter Section */}
        <FilterSection
          colorOptions={state.colorOptions}
          categoryOptions={state.categoryOptions}
          activeColorFilter={state.activeColorFilter}
          activeCategoryFilter={state.activeCategoryFilter}
          filterDropdownOpen={state.filterDropdownOpen}
          onSetColorFilter={handleSetColorFilter}
          onSetCategoryFilter={handleSetCategoryFilter}
          onClearAllFilters={handleClearAllFilters}
          onToggleFilterDropdown={handleToggleFilterDropdown}
          filterRef={filterRef}
        />

        {/* Loading and Error States */}
        {state.isLoading && (
          <div className="text-center py-10" data-testid="loading-indicator">
            <div className="inline-block w-8 h-8 border-4 border-secondary border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-2 text-text-light">Loading flowers...</p>
          </div>
        )}

        {state.error && (
          <div className="text-center py-10" data-testid="error-message">
            <p className="text-red-500">{state.error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-soft hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Flowers Grid */}
        {!state.isLoading && !state.error && (
          <FlowerGrid
            flowers={state.filteredFlowers}
            renderKey={state.renderKey}
            onQuantityChange={handleQuantityChange}
            onClearAllFilters={handleClearAllFilters}
          />
        )}

        {/* Selected Flowers Summary */}
        {state.selectedFlowers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 right-6 left-6 md:left-auto md:w-96 bg-light rounded-soft shadow-pastel-lg p-4 border border-accent z-40"
            data-testid="selected-flowers-summary"
          >
            <h3 className="font-medium text-primary mb-2">Your Selection</h3>
            <div className="max-h-40 overflow-y-auto mb-3">
              {state.selectedFlowers.map((flower) => (
                <div
                  key={flower.id}
                  className="flex justify-between items-center py-1 text-sm"
                >
                  <span>{flower.name}</span>
                  <span className="font-medium">
                    {flower.quantity} × ₹{flower.pricePerStem}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between font-medium text-primary mb-3">
              <span>Total Items:</span>
              <span>
                {state.selectedFlowers.reduce(
                  (sum, flower) => sum + flower.quantity,
                  0
                )}
              </span>
            </div>
            <div className="flex gap-2">
              <a
                href="#"
                className="inline-flex w-full items-center justify-center rounded-soft font-medium uppercase tracking-wider transition-all duration-300 shadow-pastel-sm hover:shadow-pastel-md transform hover:-translate-y-0.5 bg-blush text-text-dark hover:bg-blush/90 h-12 px-8 py-3"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSubscriptionPanel(true);
                }}
              >
                Subscribe Now
              </a>
            </div>
          </motion.div>
        )}

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={handleAuthModalClose}
          onAuthSuccess={handleAuthSuccess}
        />

        {/* Subscription Panel Modal */}
        {showSubscriptionPanel && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
            <div
              className="max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()} // Prevent clicks from closing the modal
            >
              <SubscriptionPanel
                selectedFlowers={state.selectedFlowers}
                onClose={() => {
                  setShowSubscriptionPanel(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default FeaturedFlowers;
